/**
 * Global error instrumentation for the print portal.
 *
 * Clarity showed the order-prints page logging two "JS errors": the benign
 * "ResizeObserver loop completed with undelivered notifications" (a harmless
 * browser warning — a resize handler nudged layout in the same frame; nothing
 * breaks) and bare "Script error." (a cross-origin mask — our bundle loads from
 * the Worker host, so without crossorigin+CORS the browser hides the real text).
 *
 * This module (1) SILENCES the ResizeObserver noise so it stops polluting the
 * error metric + console, and (2) REPORTS any other uncaught error / unhandled
 * rejection as `pp_js_error` (→ GA4 + Clarity) with the current step, so real
 * problems surface instead of hiding. Deduped + capped so a loop can't spam.
 */
import { track } from './analytics';

const RESIZE_OBSERVER_RE = /ResizeObserver loop/i;
const MAX_REPORTS = 10; // per page load — a broken render shouldn't fire hundreds
const seen = new Set<string>();
let reported = 0;

/** Current ordering step (0..3), best-effort — never throws. */
function currentStep(): number | undefined {
  try {
    const s = (window as unknown as { __rplStep?: number }).__rplStep;
    return typeof s === 'number' ? s : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whose script threw? (Clarity round 4, 2026-08-12.)
 *
 * This is a WINDOW listener, so on the theme embed it also catches every
 * Shopify app on the page (Klaviyo, Vitals, Gorgias, gtag…). Those are the
 * "Script error." entries: cross-origin scripts whose text the browser hides,
 * and they were 92% of the error metric — drowning out our own bugs.
 *
 * Our bundle is served SAME-ORIGIN via the app proxy (/apps/print/assets/…),
 * so a real portal error always arrives with a readable message AND a filename
 * on that path. Anything else is somebody else's script.
 *   portal      — our bundle, actionable, fix it
 *   third_party — another script on the page, named but not ours
 *   masked      — cross-origin with no detail ("Script error."), never ours
 */
function originOf(source?: string, message?: string): 'portal' | 'third_party' | 'masked' {
  const src = String(source || '');
  if (src.includes('/apps/print/assets/') || src.includes('/proxy/assets/')) return 'portal';
  if (!src || /^script error\.?$/i.test(String(message || '').trim())) return 'masked';
  return 'third_party';
}

function report(kind: string, message: string, source?: string, line?: number): void {
  try {
    const msg = String(message || '').slice(0, 200);
    const key = `${kind}:${msg}`;
    if (seen.has(key) || reported >= MAX_REPORTS) return;
    seen.add(key);
    reported += 1;
    const origin = originOf(source, message);
    track('js_error', {
      kind,
      message: msg,
      source: source ? String(source).slice(-120) : '',
      line: line ?? 0,
      step: currentStep() ?? -1,
      origin,
      // Filterable flag: own=1 is a portal bug worth chasing; own=0 is noise
      // from a third-party script we don't control.
      own: origin === 'portal' ? 1 : 0,
    });
  } catch {
    /* the reporter must never throw */
  }
}

let installed = false;

export function installErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Seed the step BEFORE anything can throw. setStep (store.ts) only writes
  // __rplStep when the step CHANGES, so an error during first render reported
  // step:-1 — which was every error in the Aug 5-11 GA4 data (101/101). The
  // store's initial step is 0; setStep overwrites this the moment they move.
  try {
    const w = window as unknown as { __rplStep?: number };
    if (typeof w.__rplStep !== 'number') w.__rplStep = 0;
  } catch {
    /* SSR/tests: no window */
  }

  window.addEventListener(
    'error',
    (e: ErrorEvent) => {
      const message = e.message || '';
      // Benign, unactionable browser warning — swallow it so it stops counting
      // as a "JS error" everywhere (Clarity, console, our reporter).
      if (RESIZE_OBSERVER_RE.test(message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return;
      }
      report('error', message || 'unknown error', e.filename, e.lineno);
    },
    true, // capture phase — see it before anything re-logs it
  );

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason as { message?: string; stack?: string } | string | undefined;
    const message =
      typeof reason === 'string' ? reason : (reason?.message ?? String(reason ?? 'unhandled rejection'));
    if (RESIZE_OBSERVER_RE.test(message)) return;
    report('unhandledrejection', message);
  });
}
